/**
 * Render a live countdown from a server `deadlineTs`. The CLIENT NEVER ENFORCES
 * time — this is display only; the server's `setAlarm` is authoritative
 * (ARCHITECTURE.md §5). We tick on rAF for smoothness but expose whole seconds.
 */

import { useEffect, useRef, useState } from 'react';
import { secondsUntil } from '../util/format';

export interface Countdown {
  /** Whole seconds remaining (rounded up). */
  readonly seconds: number;
  /** Fractional seconds remaining (for smooth ring/bar fills). */
  readonly fractional: number;
  /** True while a deadline is active and in the future. */
  readonly active: boolean;
}

export function useCountdown(deadlineTs: number | undefined): Countdown {
  const [, force] = useState(0);
  const raf = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (deadlineTs === undefined) return;
    const loop = () => {
      force((n) => (n + 1) % 1_000_000);
      if (Date.now() < deadlineTs) {
        raf.current = requestAnimationFrame(loop);
      }
    };
    raf.current = requestAnimationFrame(loop);
    return () => {
      if (raf.current !== undefined) cancelAnimationFrame(raf.current);
    };
  }, [deadlineTs]);

  const fractional = secondsUntil(deadlineTs, Date.now());
  return {
    seconds: Math.ceil(fractional),
    fractional,
    active: deadlineTs !== undefined && fractional > 0,
  };
}
